import React, { useEffect, useState } from "react";
import {
  FaMapMarkerAlt,
  FaCarSide,
  FaPhoneAlt,
  FaCalendarAlt,
} from "react-icons/fa";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const FrontPageBookingsDetails = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [bookingData, setBooking] = useState(null);
  const [isBooking, setIsBooking] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let url = process.env.REACT_APP_WEB_APP_URL + `/api/rides/${id}`;
    axios
      .get(url)
      .then((res) => {
        setBooking(res.data.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("API Error:", err);
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <h5 className="fw-bold text-secondary">⏳ Loading booking details...</h5>
      </div>
    );
  }

  if (!bookingData) {
    return (
      <div className="text-center py-5">
        <h5 className="fw-bold text-danger">No booking found.</h5>
      </div>
    );
  }

  const { booking, driver, vehicle, customers } = bookingData;

  const availableSeats = booking.seats_allowed - booking.total_booked_customer;

  document.title = "Booking Details | Sawari Online";

  const handleBooking = async () => {
    setIsBooking(true);
    try {
      let url = process.env.REACT_APP_WEB_APP_URL + `/api/booking-ride`;
      let payload = {
        booking_id: booking.id,
      };
      const token = localStorage.getItem("authToken");
      const res = await axios.post(url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success('Booking Request Send Successfully!');
    } catch (err) {
      console.error("Booking Error:", err);
      toast.error(err.response?.data?.message || "Booking failed. Try again.");
    } finally {
      setIsBooking(false);
    }
  };

  return (
    <div className="content checkout-sec py-5" style={{ backgroundColor: "#f8f9fa" }}>
      <div className="container">
        <div className="row g-4">
          {/* Left Column */}
          <div className="col-lg-8 col-md-12">
            {/* Trip Details */}
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-body">
                <h5 className="fw-bold mb-4 text-primary">🚘 Trip Details</h5>
                <p className="mb-2 text-muted">
                  <FaCalendarAlt className="me-2" />
                  {booking.booking_date} at {booking.booking_time}
                </p>
                <div className="d-flex align-items-start">
                  <div className="me-4 text-center">
                    <p className="fw-bold text-dark mb-1">{booking.status}</p>
                    <p className="text-muted small">{booking.total_kms} km</p>
                  </div>
                  <div className="flex-grow-1">
                    <p className="fw-bold mb-1">
                      <FaMapMarkerAlt className="text-success me-2" />
                      Pickup
                    </p>
                    <p className="text-muted small mb-3">{booking.pickup_addr}</p>

                    <p className="fw-bold mb-1">
                      <FaMapMarkerAlt className="text-danger me-2" />
                      Dropoff
                    </p>
                    <p className="text-muted small">{booking.dest_addr}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card shadow-sm border-0 mb-4">
              <div className="card-body">
                <h5 className="fw-bold mb-4 text-primary">Driver Details</h5>

                <div className="d-flex align-items-center mb-3 pb-3">
                  <img
                    src={driver.image}
                    alt="Driver"
                    className="rounded-circle border"
                    style={{ width: "55px", height: "55px", objectFit: "cover" }}
                  />
                  <div className="ms-3">
                    <h6 className="fw-bold mb-1">{driver.name}</h6>
                    <p className="text-muted small mb-0">
                      <FaPhoneAlt className="me-2 text-primary" />
                      {driver.phone_code} {driver.mobno}
                    </p>
                    <p className="text-muted small">{driver.email}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card shadow-sm border-0 mb-4">
              <div className="card-body">
                <h6 className="fw-bold mb-3 text-primary">📍 Tracking Status</h6>
                <ul className="list-group list-group-flush">
                  {booking.tracking.map((t, i) => (
                    <li key={i} className="list-group-item d-flex justify-content-between">
                      <span className="fw-semibold text-capitalize">{t.status}</span>
                      <span className="text-muted small">{t.datetime || "--"}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Customers */}
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-body">
                <h6 className="fw-bold mb-3 text-primary">👥 Customers</h6>
                {customers.length > 0 ? (
                  customers.map((c) => (
                    <div
                      key={c.id}
                      className="d-flex align-items-center mb-3 pb-3 border-bottom"
                    >
                      <img
                        src={c.image}
                        alt={c.name}
                        className="rounded-circle border"
                        style={{ width: "55px", height: "55px", objectFit: "cover" }}
                      />
                      <div className="ms-3">
                        <h6 className="fw-bold mb-1">{c.name}</h6>
                        <p className="text-muted small mb-0">
                          <FaPhoneAlt className="me-2 text-primary" />
                          {c.phone_code} {c.mobno}
                        </p>
                        <p className="text-muted small">{c.email}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-muted">No customers yet.</p>
                )}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="col-lg-4 col-md-12 sticky-top" style={{ top: "20px" }}>
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-body">
                <h6 className="fw-bold mb-3 text-primary">
                  <FaCarSide className="me-2" />
                  Vehicle
                </h6>
                <p className="mb-1 fw-semibold">
                  {vehicle.make_name} {vehicle.model_name} ({vehicle.year})
                </p>
                <p className="text-muted small mb-1">
                  {vehicle.type_name} - {vehicle.body_type}
                </p>
                <span className="badge bg-light text-dark border mb-2">
                  Plate: {vehicle.license_plate}
                </span>
              </div>
            </div>

            {/* Seat Booking */}
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <h6 className="fw-bold mb-2 text-primary">💺 Seat Availability</h6>
                <h5 className="fw-bold text-dark mb-1">
                  Allowed: {booking.seats_allowed}
                </h5>
                <p className="text-muted small mb-2">
                  Total Booked: {booking.total_booked_customer} | Available:{" "}
                  <span className="fw-bold text-success">{booking.seats_allowed - booking.total_booked_customer}</span>
                </p>
                <h6 className="fw-bold mb-2 text-primary">💰 Per Seat Price</h6>
                <h3 className="text-success fw-bold mb-3">Rs {vehicle.seat_rate}</h3>

                {message && (
                  <div
                    className={`alert ${message.type === "success" ? "alert-success" : "alert-danger"
                      } py-2`}
                  >
                    {message.text}
                  </div>
                )}

                <button
                  onClick={handleBooking}
                  className="btn btn-primary w-100 rounded-pill"
                  disabled={booking.seats_allowed == booking.total_booked_customer || isBooking}
                >
                  {isBooking
                    ? "⏳ Booking..."
                    : booking.seats_allowed != booking.total_booked_customer
                      ? "Book Now"
                      : "No Seats Available"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FrontPageBookingsDetails;
